import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  HostBinding,
  Input,
  Output,
  ViewChild
} from '@angular/core';

import { UmssIconComponent } from '../icon/icon.component';

@Component({
  selector: 'umss-slider-button',
  standalone: true,
  imports: [CommonModule, UmssIconComponent],
  templateUrl: './slider-button.component.html',
  styleUrls: ['./slider-button.component.scss']
})
export class UmssSliderButtonComponent implements AfterViewInit {
  @HostBinding('style.display') get hostDisplay(): string {
    return this.fullWidth ? 'block' : 'inline-block';
  }

  /** Label displayed in the track behind the thumb */
  @Input() label = 'Slide to confirm';

  /** Icon shown on the thumb (Phosphor icon name) */
  @Input() thumbIconName = 'phosphorArrowRight';

  /** Icon shown in the confirmed state */
  @Input() confirmedIconName = 'phosphorCheck';

  /** Variant styling */
  @Input() variant:
    | 'Primary'
    | 'Secondary'
    | 'Destructive'
    | 'Ghost'
    | 'Outline' = 'Primary';

  /** Size – controls height of the track and thumb */
  @Input() size: 'Mini' | 'Small' | 'Regular' | 'Large' = 'Regular';

  /** Disabled state */
  @Input() disabled = false;

  /** Loading state – disables interaction and shows spinner */
  @Input() loading = false;

  /** Whether the thumb disappears after confirmation */
  @Input() hideThumbOnConfirm = false;

  /** Percentage of the track the thumb must travel to activate (0–1) */
  @Input() threshold = 0.95;

  /** Override icon size (defaults based on button size) */
  @Input() iconSize?: string;

  /** Override icon color */
  @Input() iconColor?: string;

  /** Whether the slider stretches to fill its container */
  @Input() fullWidth = false;

  /** Emitted when the user successfully slides past the threshold */
  @Output() confirmed = new EventEmitter<void>();

  @ViewChild('track', { static: true }) trackRef!: ElementRef<HTMLDivElement>;
  @ViewChild('thumb', { static: true }) thumbRef!: ElementRef<HTMLDivElement>;

  /** Internal state */
  dragging = false;
  isConfirmed = false;
  isHinting = false;
  offsetX = 0;
  thumbWidth = 0;
  private startX = 0;
  private trackWidth = 0;
  private maxOffset = 0;
  private hintTimeout?: ReturnType<typeof setTimeout>;
  readonly trackPadding = 4;

  ngAfterViewInit(): void {
    this.thumbWidth = this.thumbRef.nativeElement.getBoundingClientRect().width;
  }

  get progressRatio(): number {
    return this.maxOffset > 0 ? this.offsetX / this.maxOffset : 0;
  }

  get labelOpacity(): number {
    return Math.max(0, 1 - this.progressRatio * 2);
  }

  get computedIconSize(): string {
    if (this.iconSize) return this.iconSize;
    switch (this.size) {
      case 'Mini':
        return '14px';
      case 'Small':
        return '16px';
      case 'Large':
        return '24px';
      default:
        return '20px';
    }
  }

  /** Hint nudge distance in pixels, proportional to track size */
  get hintOffset(): number {
    switch (this.size) {
      case 'Mini':
        return 24;
      case 'Small':
        return 32;
      case 'Large':
        return 52;
      default:
        return 42;
    }
  }

  /** Resets the slider so it can be used again */
  reset(): void {
    this.isConfirmed = false;
    this.offsetX = 0;
  }

  /** Touch / pointer helpers */
  onPointerDown(event: PointerEvent): void {
    if (this.disabled || this.loading || this.isConfirmed) return;
    event.preventDefault();

    const track = this.trackRef.nativeElement;
    const thumb = this.thumbRef.nativeElement;

    this.trackWidth = track.getBoundingClientRect().width;
    this.thumbWidth = thumb.getBoundingClientRect().width;
    this.maxOffset = this.trackWidth - this.thumbWidth - this.trackPadding * 2;
    this.startX = event.clientX;
    this.dragging = true;

    thumb.setPointerCapture(event.pointerId);
  }

  onPointerMove(event: PointerEvent): void {
    if (!this.dragging) return;
    const delta = event.clientX - this.startX;
    this.offsetX = Math.min(Math.max(0, delta), this.maxOffset);
  }

  onPointerUp(): void {
    if (!this.dragging) return;
    this.dragging = false;

    if (this.progressRatio >= this.threshold) {
      this.offsetX = this.maxOffset;
      this.isConfirmed = true;
      this.confirmed.emit();
    } else {
      this.offsetX = 0;
      // Hint when the user tapped or barely dragged — they may not know to slide
      if (this.progressRatio < 0.2) {
        this.triggerHint();
      }
    }
  }

  /** Triggered when clicking anywhere on the track (outside a drag) */
  onTrackClick(): void {
    if (this.disabled || this.loading || this.isConfirmed) return;
    this.triggerHint();
  }

  private triggerHint(): void {
    if (this.isHinting) return;
    this.isHinting = true;
    clearTimeout(this.hintTimeout);
    this.hintTimeout = setTimeout(() => {
      this.isHinting = false;
    }, 700);
  }
}
